import React, { useEffect, useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { SessionType } from '../../../../data/SessionInterface';

interface SessionTypeEditModalProps {
  visible: boolean;
  onClose: () => void;
  currentType: SessionType | string;
  onSave: (newType: SessionType) => void;
  sessionTitle?: string;
}

const SESSION_TYPE_DESCRIPTIONS: Record<SessionType, string> = {
  [SessionType.DAY]: 'Full-day program with activities throughout the day',
  [SessionType.HALF_DAY]: 'Shorter half-day sessions (morning or afternoon)',
  [SessionType.OVERNIGHT]: 'Includes overnight stay and extended programming',
  [SessionType.EXTENDED]: 'Extended hours for early drop-off or late pickup',
  [SessionType.CLINIC]: 'Focused, shorter-duration skills clinic',
};

const SessionTypeEditModal: React.FC<SessionTypeEditModalProps> = ({
  visible,
  onClose,
  currentType,
  onSave,
  sessionTitle = 'Edit Session Type',
}) => {
  const sessionTypes = Object.values(SessionType);
  const [selectedType, setSelectedType] = useState<SessionType>(
    (currentType as SessionType) || SessionType.DAY
  );

  useEffect(() => {
    if (visible) {
      setSelectedType((currentType as SessionType) || SessionType.DAY);
    }
  }, [visible, currentType]);

  const handleSave = () => {
    onSave(selectedType);
    onClose();
  };

  const handleCancel = () => {
    setSelectedType((currentType as SessionType) || SessionType.DAY);
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>{sessionTitle}</Text>
            <Pressable onPress={onClose} style={styles.closeButton}>
              <MaterialCommunityIcons name="close" size={20} color="#666" />
            </Pressable>
          </View>

          {/* Content */}
          <View style={styles.content}>
            <Text style={styles.label}>Session Type</Text>
            <Text style={styles.helperText}>Select the type that best fits this session</Text>

            <View style={styles.optionsContainer}>
              {sessionTypes.map((type) => {
                const isSelected = selectedType === type;
                return (
                  <Pressable
                    key={type}
                    style={[styles.optionItem, isSelected && styles.selectedOption]}
                    onPress={() => setSelectedType(type as SessionType)}
                  >
                    <View style={styles.optionHeader}>
                      <Text style={[styles.optionLabel, isSelected && styles.selectedOptionLabel]}>
                        {type}
                      </Text>
                      <View style={[styles.radioButton, isSelected && styles.radioButtonSelected]}>
                        {isSelected && <View style={styles.radioButtonInner} />}
                      </View>
                    </View>
                    <Text style={[styles.optionDescription, isSelected && styles.selectedOptionDescription]}>
                      {SESSION_TYPE_DESCRIPTIONS[type as SessionType]}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Pressable onPress={handleCancel} style={[styles.button, styles.cancelButton]}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </Pressable>
            <Pressable onPress={handleSave} style={[styles.button, styles.saveButton]}>
              <Text style={styles.saveButtonText}>Save</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '100%',
    maxWidth: 520,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  closeButton: {
    padding: 4,
  },
  content: {
    padding: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
    textAlign: 'center',
  },
  helperText: {
    fontSize: 12,
    color: '#666',
    marginBottom: 16,
    textAlign: 'center',
  },
  optionsContainer: {
    gap: 8,
  },
  optionItem: {
    borderWidth: 2,
    borderColor: '#e9ecef',
    borderRadius: 8,
    padding: 16,
    backgroundColor: '#f8f9fa',
    marginBottom: 8,
  },
  selectedOption: {
    borderColor: '#007bff',
    backgroundColor: '#e3f2fd',
  },
  optionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  optionLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  selectedOptionLabel: {
    color: '#007bff',
  },
  optionDescription: {
    fontSize: 12,
    color: '#666',
  },
  selectedOptionDescription: {
    color: '#0056b3',
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#ccc',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
  },
  radioButtonSelected: {
    borderColor: '#007bff',
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#007bff',
  },
  footer: {
    flexDirection: 'row',
    padding: 20,
    paddingTop: 0,
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#666',
  },
  saveButton: {
    backgroundColor: '#007bff',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: 'white',
  },
});

export default SessionTypeEditModal; 